/**
 * One module, open at whichever tab the address names.
 *
 * Keyed on the module it edits by its caller, so moving to another one arrives
 * as a fresh mount and no save, lease or conflict state outlives the module it
 * belonged to.
 *
 * @module components/studio/ModuleEditor
 */

import React, { useState } from "react";

import { hoursPerCredit, versionModalities } from "@/components/shared/effort-hours";
import {
  casComponentCodeOf,
  CasSourcePanel,
  useCasComponent,
} from "@/components/studio/CasSourcePanel";
import { MODULE_TABS, type ModuleTab } from "@/components/studio/module-editor-model";
import { ChangeCaseHandOff, ModuleEditorHeader } from "@/components/studio/ModuleEditorHeader";
import { ModuleEditorSkeleton } from "@/components/studio/ModuleEditorSkeleton";
import {
  moduleEditDecision,
  moduleLeaseRefusal,
  ModuleNotices,
  readOnlyReason,
} from "@/components/studio/ModuleNotices";
import { AssessmentsPanel } from "@/components/studio/panels/AssessmentsPanel";
import { DetailsPanel } from "@/components/studio/panels/DetailsPanel";
import { EffortPanel } from "@/components/studio/panels/EffortPanel";
import { MappingPanel } from "@/components/studio/panels/MappingPanel";
import { outcomeSources } from "@/components/studio/panels/OutcomeProvenance";
import { OutcomesPanel } from "@/components/studio/panels/OutcomesPanel";
import { ReadingPanel } from "@/components/studio/panels/ReadingPanel";
import { ReviewActions } from "@/components/studio/ReviewActions";
import { SubmissionDialog } from "@/components/studio/SubmissionDialog";
import { useModuleEditor, type UseModuleEditorOptions } from "@/components/studio/useModuleEditor";
import { Alert, Button, countLabel, EditLockProvider, LinkTabs } from "@/components/ui";
import type { MIMLO, Module } from "@/types";
import { creditSystemName } from "@/utils/award-types";

export interface ModuleEditorProps extends UseModuleEditorOptions {
  /** Which tab the address names. */
  tab: ModuleTab;
  /** `${base}/studio/${moduleId}`, which the tabs hang off. */
  tabBase: string;
  /** Every module of the programme, which a shared assessment can reach. */
  programmeModules?: readonly Module[];
  /** The programme lead, who submits a change case. */
  leadName?: string;
  /** Whether the reader is that lead. */
  readerLeads?: boolean;
}

export function ModuleEditor({
  tab,
  tabBase,
  programmeModules = [],
  leadName = undefined,
  readerLeads = false,
  ...options
}: ModuleEditorProps): React.ReactElement {
  const editor = useModuleEditor(options);
  const [submissionOpen, setSubmissionOpen] = useState(false);
  const casCode = casComponentCodeOf(editor.moduleDraft);
  const casSource = useCasComponent(casCode);

  if (editor.loading) {
    return (
      <ModuleEditorSkeleton
        tabBase={tabBase}
        label="Loading the module…"
        data-testid="module-workspace-loading"
      />
    );
  }

  const { record, moduleDraft } = editor;
  if (editor.loadError || !record || !moduleDraft) {
    return (
      <Alert tone="warning">
        <span className="flex flex-wrap items-center gap-3">
          <span>{editor.loadError?.message ?? "The module is unavailable"}</span>
          <Button variant="ghost" size="sm" onClick={editor.retryLoad}>
            Try again
          </Button>
        </span>
      </Alert>
    );
  }

  const mimlos = (moduleDraft.mimlos ?? []) as MIMLO[];
  const assessments = moduleDraft.assessments ?? [];
  const readingList = moduleDraft.readingList ?? [];
  const modalities = editor.record ? versionModalities(editor.record) : [];
  const editRefusal = editor.canEdit ? undefined : readOnlyReason(editor);
  const assessmentCount = assessments.length + (record.incomingCrossModuleAssessments ?? []).length;
  const counts: Partial<Record<ModuleTab, { count: number; noun: string }>> = {
    outcomes: { count: mimlos.length, noun: "learning outcome" },
    assessments: { count: assessmentCount, noun: "assessment" },
    reading: { count: readingList.length, noun: "reading" },
  };

  return (
    <section
      className="min-w-0 space-y-4"
      aria-labelledby="module-editor-heading"
      data-testid="module-workspace"
      // Where focus goes when the lock takes the focused control away.
      tabIndex={-1}
    >
      <SubmissionDialog
        open={submissionOpen}
        record={record}
        submitting={editor.reviewAction === "submit"}
        refusal={editRefusal}
        error={editor.reviewActionError}
        onClose={() => setSubmissionOpen(false)}
        onSubmit={(input) => {
          editor.submitForReview(input).then((done) => {
            if (done) {
              setSubmissionOpen(false);
            }
          });
        }}
      />
      <ModuleEditorHeader
        editor={editor}
        onSubmitPressed={() => {
          // The module autosaves, so nobody presses Save. Submitting flushes
          // whatever is still pending rather than refusing and telling the
          // reader to press a button the editor no longer needs them to.
          editor.save().then((saved) => {
            if (saved) {
              setSubmissionOpen(true);
            }
          });
        }}
        inChangeCase={Boolean(options.changeCaseId)}
      />
      {options.changeCaseId ? (
        <ChangeCaseHandOff leadName={leadName} readerLeads={readerLeads} />
      ) : null}
      <LinkTabs
        aria-label="Module sections"
        items={MODULE_TABS.map((entry) => {
          const tally = counts[entry.id];
          return {
            to: `${tabBase}/${entry.id}`,
            label: entry.label,
            icon: entry.icon,
            badge: tally?.count,
            badgeLabel: tally ? countLabel(tally.count, tally.noun) : undefined,
            "data-testid": `module-workspace-tab-${entry.id}`,
          };
        })}
      />
      <ModuleNotices editor={editor} />
      {casCode ? <CasSourcePanel module={moduleDraft} code={casCode} source={casSource} /> : null}
      {record.canReview ? <ReviewActions editor={editor} /> : null}

      {/* The panels lock as a programme section does: on the content's
          editability and the module's lease, with one reason for both. */}
      <EditLockProvider decision={moduleEditDecision(editor)} lease={moduleLeaseRefusal(editor)}>
        {tab === "details" ? (
          <DetailsPanel module={moduleDraft} updateDraft={editor.updateDraft} />
        ) : null}
        {tab === "outcomes" ? (
          <OutcomesPanel
            mimlos={mimlos}
            nfqLevel={record.nfqLevel}
            editor={editor}
            sources={outcomeSources(moduleDraft)}
          />
        ) : null}
        {tab === "effort" ? (
          <EffortPanel
            module={moduleDraft}
            modalities={modalities}
            hoursPerCredit={hoursPerCredit(record)}
            creditUnitLabel={creditSystemName(record.awardType, moduleDraft.credits)}
            updateEffort={editor.updateEffort}
          />
        ) : null}
        {tab === "assessments" ? (
          <AssessmentsPanel
            assessments={assessments}
            mimlos={mimlos}
            record={record}
            programmeModules={programmeModules}
            editor={editor}
          />
        ) : null}
        {tab === "mapping" ? (
          <MappingPanel
            mimlos={mimlos}
            plos={record.plos}
            ploToMimlos={editor.ploToMimlos}
            onToggle={editor.togglePloMapping}
          />
        ) : null}
        {tab === "reading" ? <ReadingPanel readingList={readingList} editor={editor} /> : null}
      </EditLockProvider>
    </section>
  );
}

export default ModuleEditor;
